import { useCallback, useRef } from 'react';
import useTracking from './useTracking';

export default function useDebugTouchEventTracking() {
  const { trackTouchStart, trackTouchEnd } = useTracking();
  const moved = useRef(false);
  const startTarget = useRef<string>();

  const handleTouchMove = useCallback(() => {
    moved.current = true;
    document.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      trackTouchEnd({
        startTarget: startTarget.current ?? 'unknown',
        isMovedFromStart: moved.current,
        target:
          event.target instanceof HTMLElement
            ? event.target.classList.toString()
            : 'unknown',
        touches: event.touches.length,
      });

      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
    },
    [trackTouchEnd, handleTouchMove],
  );

  const handleTouchCancel = useCallback(() => {
    moved.current = false;
    startTarget.current = undefined;

    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('touchmove', handleTouchMove);
  }, [handleTouchEnd, handleTouchMove]);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      startTarget.current = event?.currentTarget.classList.toString();

      trackTouchStart({
        target: startTarget.current,
        touches: event.touches.length,
      });

      moved.current = false;

      document.addEventListener('touchcancel', handleTouchCancel);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    },
    [trackTouchStart, handleTouchCancel, handleTouchMove, handleTouchEnd],
  );

  return {
    onTouchStart: handleTouchStart,
  };
}
